import { enhancedContentSourceLocation } from "./ContentSource";
import { EfficientStringSearchFunction } from "./EfficientMultiStringSearch";
import { hasCategoryAndEventName, isNetworkEvent, TabEvent } from "./Events";
import { InstrumentationEventDomEventFired, isDomEventFiredEvent } from "./Events/InstrumentationEvent";
import { EfficientEncodedStringSearchFunction, EfficientEncodedStringSearchResult, getSearchFunctionForPotentiallyEncodedStrings } from "./Search/getEncodingsToSearchFor";
import { urlToDomain } from "./utils/domains";
import { DbRecordedEvent } from "../State/EvidenceDB/DatabaseTypes";
import { eventToContentSources } from "../ServiceWorker/networkEventToContentSources";
import { DbNetworkEventWithSearchResults, SearchContextOptions, NetworkSearchScopeOptions, SearchTermsArgument, ContentSourceSearchResult } from "./searchRecordedEvent";

export type DbUserActionEvent = DbRecordedEvent<InstrumentationEventDomEventFired | TabEvent>;

export type SearchResultEvent = DbUserActionEvent | DbNetworkEventWithSearchResults

export interface SearchOptions extends SearchContextOptions, NetworkSearchScopeOptions, SearchTermsArgument {
	maxResultsPerSearchTerm?: number;
	includeUserEvents?: boolean; // defaults to true
}
export class DbEventSearcher {
	results: SearchResultEvent[] = [];

	constructor(
		protected searchFunction: EfficientEncodedStringSearchFunction,
		protected options: SearchOptions
	) { }

	static createDbEventSearcher = async (options: SearchOptions) => {
		const searchFunction = await getSearchFunctionForPotentiallyEncodedStrings([...options.searchTerms]);
		return new DbEventSearcher(searchFunction, options);
	}

	get maxResultsPerSearchTerm() {
		return this.options.maxResultsPerSearchTerm ?? 1000;
	}

	get includeUserEvents() {
		return this.options.includeUserEvents ?? true;
	}

	protected totalEvents: number = 0;
	protected connectionsProcessed: number = 0;
	protected eventsProcessed: number = 0;
	protected charactersProcessed: number = 0;
	protected resultsGeneratedPerSearchTerm: { [key: string]: number; } = {};
	protected termOriginDestinationFieldSeen = new Set<string>();

	searchContentOfEvents = (events: DbRecordedEvent[]) => {
		for (const event of events) {
			this.searchContentOfEvent(event);
		}
	};

	searchContentOfEvent = (event: DbRecordedEvent): ContentSourceSearchResult[] => {
		if (this.includeUserEvents && isDomEventFiredEvent(event)) {
			this.results.push(event);
			return [];
		} else if (isNetworkEvent(event)) {
			if (hasCategoryAndEventName('Debugger.Network', "Network.requestWillBeSent")(event) ||
				hasCategoryAndEventName('Debugger.Network', "Network.webSocketCreated")(event)) {
				this.connectionsProcessed++;
			}
			const contentSourceSearchResults = [] as ContentSourceSearchResult[];
			const contentSources = eventToContentSources(event);
			const {
				prefixSize = 72, suffixSize = 72, searchDataReceived = false,
			} = this.options;
			// if (contentSources.length > 0) {
			// 	ProcessInfo.log("Searching through content sources", contentSources);
			// }
			for (const contentSource of contentSources) {
				// Only search through content sent out
				if (!searchDataReceived && contentSource.direction === 'received') {
					continue;
				}
				// Update progress counters
				this.charactersProcessed += contentSource.content.length;
				const { tab, destination } = event;

				// Search the source to find new results
				const newResultsFoundFromSearch = this.searchFunction(contentSource.content).map((result) => {
					const { encodedSearchTerm, originalSearchTerm, position } = result;
					const key = `${event.raw.requestId}${tab.url}\0${urlToDomain(destination.url)}\0${enhancedContentSourceLocation(contentSource)}\0${originalSearchTerm}\0${encodedSearchTerm}`;
					// Reduce information overload by tracking keeping only one instance a search term data going from a->b via a given field
					if (this.termOriginDestinationFieldSeen.has(key)) return;
					this.termOriginDestinationFieldSeen.add(key);
					const numberOfResultsForThisSearchTerm = this.resultsGeneratedPerSearchTerm[originalSearchTerm] =
						(this.resultsGeneratedPerSearchTerm[originalSearchTerm] ?? 0) + 1;
					if (numberOfResultsForThisSearchTerm > this.maxResultsPerSearchTerm) return;
					const prefix = contentSource.content.slice(Math.max(0, position - prefixSize), position);
					const suffix = contentSource.content.slice(position + encodedSearchTerm.length, position + encodedSearchTerm.length + suffixSize);
					const inferredDataTypeName = position > 1 ? undefined :
						contentSource.location === "url:query:value" ? contentSource.queryKey :
							contentSource.location === "body:form:value" ? contentSource.key :
								contentSource.location === "body:json:value" ? contentSource.key :
									contentSource.location === "cookie:value" ? contentSource.cookieName :
										undefined;
					return {
						prefix, suffix,
						...(inferredDataTypeName == null ? {} : { inferredDataTypeName }),
						...result, ...contentSource
					} satisfies ContentSourceSearchResult;
				}).filter(x => x != null);
				contentSourceSearchResults.push(...newResultsFoundFromSearch);
			}
			if (contentSourceSearchResults.length > 0) {
				this.results.push({ ...event, searchResults: contentSourceSearchResults });
			}
			return contentSourceSearchResults;
		} else {
			return [];
		}
	};
}
